import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import apiEndpoints from 'api/endpoints';
import clsx from 'clsx';
import { COMPANY_AGREEMENT_STATUS, voidDocumentStatus } from 'helper/constant';
import { documentSigningSteps, oneDayAgo } from 'helper/utility';
import { usePollApi } from 'hooks/usePollApi';
import PropTypes from 'prop-types';

import {
    signDocumentStatusAction,
    signDocumentInitiateAction,
    signDocumentSessionAction,
    documentSignedDateAction,
    clearDocumentSignedDateAction,
} from 'redux/actions/apiCall';
import { refreshCompanyAction } from 'redux/actions/auth';
import { openToast as openToastCreator } from 'redux/actions/toaster';

import { ReactComponent as WarrantyIcon } from 'asset/CustomIcons/warranty.svg';
import { ReactComponent as CheckIcon } from 'asset/IconsPack/at_check-circle.svg';
import { ReactComponent as ExpandIcon } from 'asset/IconsPack/at_chevron-down-filled.svg';

//components
import AgreementSignButton from './components/AgreementSignButton';

function SignDocument(props) {
    const { classes, isSubAccordionExpanded, handleSubAccordionChange, totalSteps } = props;

    const dispatch = useDispatch();
    const token = useSelector((state) => state.auth?.userData?.token);
    const document = useSelector((state) => state.apiCall?.signDocument || {});
    const companyData = useSelector((state) => state.auth?.userData?.user.company || {});
    const companyUUID = useSelector((state) => state.auth?.userData?.user.company.uuid);

    const openToast = useCallback((...args) => dispatch(openToastCreator(...args)), [dispatch]);
    const { CREATION_INPROGRESS } = COMPANY_AGREEMENT_STATUS;

    const pollingProps = useMemo(() => {
        return {
            apiUrl: apiEndpoints.signDocumentStatus,
            tokenType: 'secret_token',
            method: 'get',
            pollingInterval: 6000,
            exponentialBackOff: true,
            maxAttempts: 10,
        };
    }, []);

    const agreementCreationInProgress = useMemo(() => {
        return companyData?.agreementCreationStatus === CREATION_INPROGRESS;
    }, [companyData?.agreementCreationStatus, CREATION_INPROGRESS]);

    const { data, startPolling, stopPolling, resetPolling, isFetching } = usePollApi();

    const [loading, setLoading] = useState(false);
    const documentInitiatedRef = useRef(false);

    const documentDetails = useMemo(() => {
        let value = { ...document };

        if (data && value?.status?.documentStatus !== 'approved') {
            value = { ...document, status: { ...data } };
        }

        return value;
    }, [document, data]);

    const onDocumentSigning = () => {
        setLoading(true);
        startPolling(pollingProps);
    };

    // The agreement creation process may take 1 to 2 minutes for some customers.
    // For those customers, we poll our back-end at 4-second intervals to see the status
    // Once the agreement is ready, the customer will be able to proceed with signing.
    const onDocumentCreation = useCallback(() => {
        const pollingPropsForCreation = {
            apiUrl: apiEndpoints.signDocumentStatus,
            tokenType: 'secret_token',
            method: 'get',
            pollingInterval: 4000,
            exponentialBackOff: true,
            maxAttempts: 10,
        };

        setLoading(true);
        startPolling(pollingPropsForCreation);
    }, [startPolling]);

    const handleDraftDocument = useCallback(
        (documentReadyForSigning) => {
            if (!documentDetails?.session?.sessionId && !documentInitiatedRef?.current) {
                if (documentReadyForSigning) {
                    dispatch(
                        signDocumentInitiateAction({ loader: true }, token, () => {
                            dispatch(signDocumentSessionAction({ loader: true }, token, () => {}));
                        })
                    );
                }

                documentInitiatedRef.current = true;
            }
        },
        [documentDetails?.session?.sessionId, token, dispatch]
    );

    useEffect(() => {
        const { meta_data, status } = companyData;

        const alreadyApprovedUser =
            !['initiated', 'awaiting_user', 'awaiting_admin'].includes(
                meta_data?.agreement_status
            ) && status !== 'pending';
        const newUserWithoutDocument = !meta_data && status === 'pending';
        const accountNotActive = ['rejected', 'revoked'].includes(status);

        if (alreadyApprovedUser || newUserWithoutDocument || accountNotActive) return;

        dispatch(signDocumentStatusAction({ loader: true }, token, () => {}));
    }, [companyData, token, dispatch]);

    useEffect(() => {
        const { documentReadyForSigning, pandaDocStatus, documentStatus } =
            documentDetails.status || {};

        if (loading && ['awaiting_user', 'initiated'].includes(documentStatus)) {
            return;
        }

        if (documentStatus === 'awaiting_admin') {
            setLoading(false);
            stopPolling();
            return;
        }

        if (voidDocumentStatus.includes(pandaDocStatus)) {
            openToast({
                variant: 'error',
                message:
                    'Agreement Document has expired. Please contact support at support@authenticate.com for further assistance.',
            });
            return;
        }

        switch (pandaDocStatus) {
            case 'document.draft':
                stopPolling();
                handleDraftDocument(documentReadyForSigning);
                break;

            case 'document.viewed':
            case 'document.sent': {
                const expire = documentDetails?.session?.expiresAt;
                const currentTime = Date.now();
                const expireTime = expire ? new Date(expire).getTime() : null;

                if (!expire || currentTime >= expireTime) {
                    dispatch(signDocumentSessionAction({ loader: true }, token, () => {}));
                }
                break;
            }
            case 'document.completed':
                if (companyData.status === 'pending' && !documentDetails?.signedDate) {
                    dispatch(documentSignedDateAction(Date.now()));
                    dispatch(
                        refreshCompanyAction({
                            token,
                            companyUUID,
                        })
                    );
                }
                break;
            default:
                break;
        }
    }, [
        documentDetails,
        stopPolling,
        token,
        loading,
        companyData.status,
        companyUUID,
        openToast,
        dispatch,
        handleDraftDocument,
    ]);

    useEffect(() => {
        const hours = documentDetails?.signedDate ? oneDayAgo(documentDetails.signedDate) : false;

        if (hours > 24) {
            dispatch(clearDocumentSignedDateAction());
        }
    }, [documentDetails, dispatch]);

    useEffect(() => {
        if (window?.localStorage) {
            const closedSigningTab = window.localStorage.getItem('signClosed');
            if (closedSigningTab) {
                setLoading(false);
                stopPolling();
                window.localStorage.removeItem('signClosed');
            }
        }
    }, [data, stopPolling]);

    useEffect(() => {
        if (agreementCreationInProgress) {
            onDocumentCreation();
        }
    }, [agreementCreationInProgress, onDocumentCreation]);

    useEffect(() => {
        const agreementCreated =
            agreementCreationInProgress && documentDetails?.status?.documentReadyForSigning;

        if (agreementCreated) {
            setLoading(false);

            resetPolling();

            dispatch(
                refreshCompanyAction({
                    token,
                    companyUUID,
                })
            );
        }
    }, [
        agreementCreationInProgress,
        companyUUID,
        token,
        documentDetails?.status?.documentReadyForSigning,
        resetPolling,
        dispatch,
    ]);

    const { content, canShow, title, link, linkText, isExternalLink, disabled, status } =
        documentSigningSteps(documentDetails, companyData);

    return (
        <Accordion
            className={classes.subAccordionRoot}
            expanded={isSubAccordionExpanded}
            onChange={handleSubAccordionChange}
        >
            <AccordionSummary
                className={classes.subAccordionSummary}
                expandIcon={<ExpandIcon className={classes.expandIcon} />}
            >
                <Box className={classes.subAccordionHeader}>
                    <Typography className={classes.subAccordionHeaderTitle}>
                        {status === 'completed' ? (
                            <CheckIcon
                                className={clsx(classes.mainSubHeaderIcon, classes.success)}
                            />
                        ) : (
                            <WarrantyIcon className={classes.mainSubHeaderIcon} />
                        )}{' '}
                        {title}
                    </Typography>
                    {canShow && (
                        <AgreementSignButton
                            link={link}
                            linkText={linkText}
                            isExternalLink={isExternalLink}
                            onDocumentSign={onDocumentSigning}
                            disabled={disabled}
                            isSigning={isFetching}
                        />
                    )}
                </Box>
                <Typography className={classes.mainAccordionHeaderSubtitle}>
                    Step {1} of {totalSteps}
                </Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.subAccordionDetails}>
                <Box className={classes.subAccordionDetailsContainer}>
                    <Typography className={classes.subAccordionDetailsText}>{content}</Typography>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
}

SignDocument.propTypes = {
    classes: PropTypes.object,
    isSubAccordionExpanded: PropTypes.bool,
    handleSubAccordionChange: PropTypes.func,
    totalSteps: PropTypes.number,
};

export default SignDocument;
